import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import heroPoster from "@/assets/hero-poster.jpg?w=640;768;1024;1280;1440;1600;1920&format=webp&as=srcset";
import heroPosterFallback from "@/assets/hero-poster.jpg?w=1440&format=jpg";
import heroMen from "@/assets/hero-men-cognac.jpg?w=640;768;1024;1280;1440;1600;1920&format=webp&as=srcset";
import heroMenFallback from "@/assets/hero-men-cognac.jpg?w=1440&format=jpg";
import heroWomen from "@/assets/hero-women-atelier.jpg?w=640;768;1024;1280;1440;1600;1920&format=webp&as=srcset";
import heroWomenFallback from "@/assets/hero-women-atelier.jpg?w=1440&format=jpg";
import { Monogram } from "@/components/site/Monogram";

const SLIDES = [
  {
    src: heroPosterFallback,
    webp: heroPoster,
    alt: "A confident model in a cognac full-grain leather jacket — KAKAIYS campaign",
    variant: "a",
  },
  {
    src: heroMenFallback,
    webp: heroMen,
    alt: "KAKAIYS — handcrafted luxury leather, men's cognac jacket portrait",
    variant: "b",
  },
  {
    src: heroWomenFallback,
    webp: heroWomen,
    alt: "KAKAIYS — women's atelier portrait in soft beige lambskin coat",
    variant: "c",
  },
] as const;

const SLIDE_DURATION_MS = 9000;

/**
 * KAKAIYS hero — cinematic luxury crossfade carousel with Ken Burns motion.
 * Three editorial frames rotate every 9s with a 1.6s ease crossfade.
 * Only the first slide loads eagerly; others preload after first paint to protect LCP.
 */
export function HeroVideo() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    // Preload the remaining slides once the page is interactive
    SLIDES.slice(1).forEach((s) => {
      const link = document.createElement("link");
      link.rel = "preload";
      link.as = "image";
      link.href = s.src;
      document.head.appendChild(link);
    });

    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer) return;
      timer = setInterval(() => {
        setIndex((i) => (i + 1) % SLIDES.length);
      }, SLIDE_DURATION_MS);
    };
    const stop = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVis = () => (document.hidden ? stop() : start());

    start();
    document.addEventListener("visibilitychange", onVis);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVis);
    };
  }, []);

  return (
    <section className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-bone">
      <div className="absolute inset-0">
        {SLIDES.map((slide, i) => {
          const active = i === index;
          const eager = i === 0;
          return (
            <div
              key={slide.src}
              className={`hero-slide hero-slide-${slide.variant} ${active ? "is-active" : ""}`}
              aria-hidden={!active}
            >
              <picture>
                <source
                  type="image/webp"
                  srcSet={slide.webp}
                  sizes="100vw"
                />
                <img
                  src={slide.src}
                  alt={slide.alt}
                  width={1920}
                  height={1080}
                  loading={eager ? "eager" : "lazy"}
                  decoding="async"
                  fetchPriority={eager ? "high" : "low"}
                />
              </picture>
            </div>
          );
        })}
      </div>

      {/* Editorial gradient — guarantees text legibility on any imagery */}
      <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-ink/40 to-ink/20" />
      <div className="absolute inset-0 bg-gradient-to-r from-ink/55 via-transparent to-transparent" />

      <div className="relative z-10 flex h-full flex-col justify-end pb-20 md:pb-28">
        <div className="container-luxe text-bone">
          <div className="reveal flex items-center gap-3 mb-6">
            <Monogram className="h-7 w-7 opacity-95 text-gold-soft" strokeWidth={6} />
            <span className="eyebrow text-gold-soft">KAKAIYS · Maison of Leather</span>
          </div>

          <h1 className="reveal reveal-delay-1 font-serif font-light text-[11vw] leading-[1.02] sm:text-6xl md:text-7xl lg:text-[6rem] xl:text-[7rem] max-w-5xl tracking-[-0.01em] text-bone drop-shadow-[0_2px_24px_rgba(20,16,12,0.35)]">
            Handcrafted Leather.
            <br />
            <span className="italic">Built for Generations.</span>
          </h1>

          <p className="reveal reveal-delay-2 mt-8 max-w-md text-sm md:text-base text-bone leading-relaxed font-light tracking-wide drop-shadow-[0_1px_12px_rgba(20,16,12,0.45)]">
            Full-grain hides, finished by hand in our Sialkot atelier.
            Worn today, inherited tomorrow.
          </p>

          <div className="reveal reveal-delay-3 mt-12 flex flex-wrap gap-4">
            <Link
              to="/shop"
              preload="intent"
              className="inline-flex items-center justify-center bg-bone text-ink px-12 py-4 text-[11px] uppercase tracking-[0.32em] hover:bg-gold-soft transition-colors duration-500"
            >
              Discover the Collection
            </Link>
            <Link
              to="/our-story"
              preload="intent"
              className="inline-flex items-center justify-center border border-bone/70 text-bone px-12 py-4 text-[11px] uppercase tracking-[0.32em] hover:bg-bone hover:text-ink transition-colors duration-500"
            >
              The Maison
            </Link>
          </div>
        </div>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-8 right-6 md:right-12 z-10 flex gap-3">
        {SLIDES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setIndex(i)}
            aria-label={`Show slide ${i + 1}`}
            aria-current={i === index}
            className={`h-[2px] transition-all duration-700 ${
              i === index ? "w-10 bg-gold-soft" : "w-6 bg-bone/40 hover:bg-bone/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
